import type { AxiosError, AxiosRequestConfig, InternalAxiosRequestConfig } from "axios";
import axios from "axios";
import { fetchAuthSession } from "aws-amplify/auth";
import { installLoyaltyMock } from "./api/loyalty/mock";

const baseURL = import.meta.env.VITE_LOYALTY_API_URL;
// Tant que le backend loyalty n'est pas déployé : VITE_LOYALTY_USE_MOCK=true sert
// le contrat openapi.yaml depuis localStorage. Passer à false pour brancher le vrai backend.
const useMock = import.meta.env.VITE_LOYALTY_USE_MOCK === "true";

// Définir un type pour la fonction de snackbar
type ShowSnackbarFn = (message: string, type: "success" | "error" | "info" | "warning") => void;

// Variable globale pour stocker la référence à la fonction
let showSnackbar: ShowSnackbarFn | null = null;

// Fonction pour enregistrer la fonction de snackbar
export const registerLoyaltySnackbar = (fn: ShowSnackbarFn): void => {
  showSnackbar = fn;
};

export const HTTPClientLoyalty = axios.create({ baseURL });

/**
 * loyalty-api (customer + POS), a different API from the management one above.
 * Only redeem-by-scan uses it: that route lives there, not in saas, and its
 * CognitoSO authorizer is the saas user pool — so the same merchant idToken
 * works and the interceptors below are shared.
 */
export const HTTPClientLoyaltyCustomer = axios.create({
  baseURL: import.meta.env.VITE_LOYALTY_CUSTOMER_API_URL,
});

if (useMock) {
  installLoyaltyMock(HTTPClientLoyalty);
  installLoyaltyMock(HTTPClientLoyaltyCustomer);
}

const attachIdToken = async (config: InternalAxiosRequestConfig) => {
  if (useMock) return config;
  const { idToken } = (await fetchAuthSession()).tokens ?? {};
  if (idToken) {
    config.headers.set("Authorization", `Bearer ${idToken.toString()}`);
  }
  return config;
};

HTTPClientLoyalty.interceptors.request.use(attachIdToken);
HTTPClientLoyaltyCustomer.interceptors.request.use(attachIdToken);

const reportError = (error: AxiosError) => {
  if (error.message === "Network Error") {
    showSnackbar?.("Erreur de réseau. Si le problème persiste, contactez le support.", "error");
    return Promise.reject(error);
  }

  const code = (error.response?.data as { code?: string } | undefined)?.code;

  if (error.response?.status === 401) {
    showSnackbar?.("Vous êtes deconnecté. Veuillez-rafraichir la page.", "warning");
  } else if (code === "PLACE_NOT_ALLOWED" || error.response?.status === 403) {
    showSnackbar?.("Vous n'avez pas accès à cet établissement.", "error");
  }

  return Promise.reject(error);
};

HTTPClientLoyalty.interceptors.response.use((response) => response, reportError);
HTTPClientLoyaltyCustomer.interceptors.response.use((response) => response, reportError);

export const swrFetcherLoyalty = (config: AxiosRequestConfig) =>
  HTTPClientLoyalty.request(config).then((res) => res.data);
